import React from 'react'

/**
 * Small decorative graph mark used as ambient ornamentation in the empty
 * space alongside a title block. It is intentionally:
 *
 *   - Static & non-interactive (`pointer-events: none`, `aria-hidden`).
 *   - Sized small (caller controls width/height via className).
 *   - Faded on all four edges via a radial mask so it dissolves
 *     softly into the page background.
 *   - Drawn from a fixed graph composition so it never feels random.
 */
interface NetworkBackdropProps {
  className?: string
}

interface Node {
  id: string
  x: number
  y: number
  /** Visual size in px: 'sm' (1.5) | 'md' (2) | 'lg' (2.5). */
  size?: 'sm' | 'md' | 'lg'
  /** Render with the signal accent. Use sparingly. */
  accent?: boolean
}

interface Edge {
  from: string
  to: string
}

// A more network-like composition in a 260×120 viewBox:
//   * One cluster of 4-5 nodes on the left, one on the right.
//   * Bridge lines crossing the middle.
//   * Single rust accent in the right cluster.
const NODES: Node[] = [
  // Left cluster
  { id: 'a', x: 35, y: 32, size: 'sm' },
  { id: 'b', x: 60, y: 70, size: 'md' },
  { id: 'c', x: 95, y: 38, size: 'sm' },
  { id: 'd', x: 75, y: 95, size: 'sm' },
  { id: 'e', x: 120, y: 78, size: 'sm' },

  // Middle bridge
  { id: 'm1', x: 150, y: 50, size: 'sm' },
  { id: 'm2', x: 165, y: 95, size: 'sm' },

  // Right cluster
  { id: 'r1', x: 195, y: 30, size: 'sm' },
  { id: 'r2', x: 215, y: 70, size: 'lg', accent: true },
  { id: 'r3', x: 235, y: 25, size: 'sm' },
  { id: 'r4', x: 240, y: 95, size: 'md' },
]

const EDGES: Edge[] = [
  // Inside left cluster
  { from: 'a', to: 'b' },
  { from: 'a', to: 'c' },
  { from: 'b', to: 'c' },
  { from: 'b', to: 'd' },
  { from: 'd', to: 'e' },
  { from: 'c', to: 'e' },

  // Left → middle
  { from: 'c', to: 'm1' },
  { from: 'e', to: 'm2' },

  // Middle → right
  { from: 'm1', to: 'r1' },
  { from: 'm1', to: 'r2' },
  { from: 'm2', to: 'r2' },
  { from: 'm2', to: 'r4' },

  // Inside right cluster
  { from: 'r1', to: 'r2' },
  { from: 'r1', to: 'r3' },
  { from: 'r2', to: 'r3' },
  { from: 'r2', to: 'r4' },
  { from: 'r3', to: 'r4' },
]

const SIZE_PX = { sm: 1.5, md: 2, lg: 2.5 } as const

// Radial mask that fades the graph at every edge — strong center,
// transparent corners. Looks soft against any background.
const EDGE_FADE_MASK =
  'radial-gradient(ellipse at center, #000 30%, rgba(0,0,0,0.6) 60%, transparent 100%)'

const NetworkBackdrop: React.FC<NetworkBackdropProps> = ({ className = '' }) => {
  const byId = Object.fromEntries(NODES.map((n) => [n.id, n]))

  return (
    <div
      aria-hidden
      className={`pointer-events-none ${className}`}
      style={{
        WebkitMaskImage: EDGE_FADE_MASK,
        maskImage: EDGE_FADE_MASK,
      }}
    >
      <svg
        viewBox="0 0 260 120"
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full"
        role="presentation"
      >
        <g
          stroke="currentColor"
          className="text-ink-300"
          strokeWidth={0.45}
          opacity={0.85}
        >
          {EDGES.map(({ from, to }, idx) => {
            const a = byId[from]
            const b = byId[to]
            return (
              <line key={`e-${idx}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
            )
          })}
        </g>

        <g>
          {NODES.map((n) => (
            <circle
              key={n.id}
              cx={n.x}
              cy={n.y}
              r={SIZE_PX[n.size ?? 'md']}
              className={n.accent ? 'text-signal' : 'text-ink-400'}
              fill="currentColor"
              opacity={n.accent ? 0.9 : 0.6}
            />
          ))}
        </g>
      </svg>
    </div>
  )
}

export default NetworkBackdrop
